import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Button from '../../ui/Button';
import ButtonsGroup from '../../ui/ButtonsGroup';
import { useTranslation } from 'react-i18next';
import { GAME_DB } from '../../db/game';
import { generateIdRoutePath, getAtomFormattedDate } from '../../helpers';
import { getGameState, sendTaskAnswer, sendTaskAnswersTimeLogsList, } from '../../api/game-mode/game';
import { addTaskAnswer, selectCurrentTaskTimeLogs, setCurrentTaskTimeLogs, setGame, setTaskStatus, updateGameTotalPoints, } from '../../store/slices/rallyGame';
import { useAppDispatch } from '../../store';
import { APP_ROUTES } from '../../app.config.vars/routes';
import { useNavigate } from 'react-router-dom';
import { API_ERRORS } from '../../app.config.vars/errorCodes';
import { useSelector } from 'react-redux';
import { blobToDataUrl, reportMemoryCanary, reportQuotaCanary, } from '../../helpers/storageTelemetry';
const GameplayControls = ({ rallyPathId, isLinearRally, showTaskResult, setShowTaskResult, currentTaskId, currentTaskInfo, currentTaskResult, currentInputTaskAnswer, currentSelectionTaskAnswers, currentPhotoUploadBlob, currentPhotoUploadMimeType, validateInputField, validateSelect, goToNextTask, gameStatus, showLoader, setShowLoader, }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const currentTaskTimeLogs = useSelector(selectCurrentTaskTimeLogs);
    const checkIfTaskAnswerIsCorrect = (selectedAnswers, answers) => {
        const correctAnswerIds = answers
            .filter((answer) => answer.isCorrect)
            .map((answer) => answer.uid)
            .sort((a, b) => a - b);
        const selectedAnswersIds = selectedAnswers.map((answer) => answer.uid).sort((a, b) => a - b);
        return (correctAnswerIds.length === selectedAnswersIds.length &&
            correctAnswerIds.every((uid, index) => uid === selectedAnswersIds[index]));
    };
    const logCurrentTaskTime = async () => {
        if (!currentTaskTimeLogs) {
            return;
        }
        const timeLogs = {
            task: currentTaskTimeLogs.taskUid,
            startTime: currentTaskTimeLogs.startTime,
            endTime: getAtomFormattedDate(Date.now()),
        };
        const response = await sendTaskAnswersTimeLogsList({ timeLogs: [timeLogs] });
        if (response.error) {
            await GAME_DB.UPDATE.pushAnswerTimeLogs(timeLogs);
        }
        dispatch(setCurrentTaskTimeLogs(null));
    };
    const updateLocalTaskStatus = async (status) => {
        if (currentTaskResult?.status === 'new') {
            const { newStatus } = status;
            await GAME_DB.UPDATE.taskStatus({
                uid: currentTaskId,
                status: newStatus,
            });
            dispatch(setTaskStatus({
                taskUid: currentTaskId,
                status: newStatus,
            }));
        }
    };
    const updateLocalTaskResult = async (answerPayload, newStatus, pointsEarned) => {
        await GAME_DB.UPDATE.pushLocalAnswer({
            task: currentTaskId,
            answerType: currentTaskInfo.answerType,
            ...answerPayload,
        });
        await GAME_DB.UPDATE.taskResult({
            taskId: currentTaskId,
            status: newStatus,
            pointsEarned,
            newAnswer: { ...answerPayload, pointsEarned },
        });
        dispatch(setTaskStatus({ taskUid: currentTaskId, status: newStatus }));
        dispatch(addTaskAnswer({ taskUid: currentTaskId, answer: { ...answerPayload, pointsEarned } }));
        dispatch(updateGameTotalPoints({ lastEarnedPoints: pointsEarned }));
    };
    const updateGameState = async (localUpdateFn, stateUpdateFn) => {
        const gameState = await getGameState();
        if (gameState.data) {
            await GAME_DB.UPDATE.gameState(gameState.data);
            dispatch(setGame(gameState.data));
            stateUpdateFn && stateUpdateFn();
        }
        else {
            await localUpdateFn();
            stateUpdateFn && stateUpdateFn();
        }
    };
    const updateLocalInputTaskResult = async () => {
        await updateLocalTaskResult({ answerValue: currentInputTaskAnswer }, 'completed', currentTaskInfo.successPoints);
    };
    const updateLocalValidatedInputTaskResult = async () => {
        const correctAnswers = currentTaskInfo.answers.map((answer) => answer.content);
        const isTaskAnswerCorrect = correctAnswers.some((answer) => answer.toLowerCase().trim() === currentInputTaskAnswer.toLowerCase().trim());
        const newStatus = isTaskAnswerCorrect
            ? 'completed'
            : currentTaskResult.playerAnswers.length < 2
                ? 'failed_attempt'
                : 'failed';
        const pointsEarned = isTaskAnswerCorrect
            ? currentTaskInfo.successPoints
            : 0 - currentTaskInfo.failurePoints;
        await updateLocalTaskResult({ answerValue: currentInputTaskAnswer }, newStatus, pointsEarned);
    };
    const handleInputTaskAnswerSubmit = async () => {
        const response = await sendTaskAnswer({
            task: currentTaskInfo.uid,
            answerType: currentTaskInfo.answerType,
            answerValue: currentInputTaskAnswer,
        });
        if (response.error?.code === API_ERRORS.noInternet) {
            if (currentTaskInfo.answerType === 'text') {
                await updateLocalInputTaskResult();
            }
            else if (currentTaskInfo.answerType === 'validated-text') {
                await updateLocalValidatedInputTaskResult();
            }
        }
        else {
            await updateGameState(currentTaskInfo.answerType === 'text'
                ? updateLocalInputTaskResult
                : updateLocalValidatedInputTaskResult);
        }
    };
    const updateLocalPhotoUploadTaskResult = async (mediaReference) => {
        await updateLocalTaskResult({ mediaReference }, 'completed', currentTaskInfo.successPoints);
    };
    const handlePhotoUploadTaskAnswerSubmit = async () => {
        await reportQuotaCanary('photo-submit');
        reportMemoryCanary('photo-submit');
        if (!currentPhotoUploadBlob) {
            return;
        }
        // Persist the Blob in the dedicated IDB store first so it survives a tab
        // crash or OOM kill between here and the POST. No base64 materialized yet.
        await GAME_DB.UPDATE.pushTaskUploadBlob({
            taskId: currentTaskInfo.uid,
            blob: currentPhotoUploadBlob,
            mimeType: currentPhotoUploadMimeType ?? currentPhotoUploadBlob.type ?? 'application/octet-stream',
        });
        // Build the base64 only at the moment of POST. It lives in `mediaReference`
        // for the duration of this function, then is released for GC.
        const mediaReference = await blobToDataUrl(currentPhotoUploadBlob);
        const response = await sendTaskAnswer({
            task: currentTaskInfo.uid,
            answerType: currentTaskInfo.answerType,
            mediaReference,
        });
        if (response.error?.code === API_ERRORS.noInternet) {
            await updateLocalPhotoUploadTaskResult(mediaReference);
        }
        else {
            await updateGameState(() => updateLocalPhotoUploadTaskResult(mediaReference));
        }
    };
    const updateLocalSelectionTaskResult = async (selectedAnswers, selectedAnswersStr) => {
        let newStatus, pointsEarned;
        if (currentTaskInfo) {
            if (currentTaskInfo.answerType === 'player-selection') {
                newStatus = 'completed';
                pointsEarned = currentTaskInfo.successPoints;
            }
            else {
                const isAnswerCorrect = checkIfTaskAnswerIsCorrect(selectedAnswers, currentTaskInfo.answers);
                newStatus = isAnswerCorrect ? 'completed' : 'failed';
                pointsEarned = isAnswerCorrect
                    ? currentTaskInfo.successPoints
                    : 0 - currentTaskInfo.failurePoints;
            }
            await updateLocalTaskResult({ answerValue: selectedAnswersStr }, newStatus, pointsEarned);
        }
    };
    const handleSelectionTaskAnswerSubmit = async (selectedAnswers) => {
        const selectedAnswersStr = selectedAnswers.map((answer) => answer.uid).join(',');
        const response = await sendTaskAnswer({
            task: currentTaskInfo.uid,
            answerType: currentTaskInfo.answerType,
            answerValue: selectedAnswersStr,
        });
        if (response.error?.code === API_ERRORS.noInternet) {
            await updateLocalSelectionTaskResult(selectedAnswers, selectedAnswersStr);
        }
        else {
            await updateGameState(() => updateLocalSelectionTaskResult(selectedAnswers, selectedAnswersStr));
        }
    };
    const onTaskSkipButtonClick = async () => {
        setShowLoader(true);
        await logCurrentTaskTime();
        await updateGameState(() => updateLocalTaskStatus({ newStatus: 'skipped' }));
        setShowLoader(false);
        goToNextTask();
    };
    const onTaskNextButtonClick = async () => {
        if (showTaskResult) {
            setShowTaskResult(false);
            if (gameStatus === 'active') {
                goToNextTask();
            }
            else {
                navigate(generateIdRoutePath(APP_ROUTES.rallyGameResult, rallyPathId));
            }
        }
        else {
            if (currentTaskResult?.status !== 'completed') {
                switch (currentTaskInfo?.taskType) {
                    case 'information':
                        setShowLoader(true);
                        await logCurrentTaskTime();
                        await updateGameState(() => updateLocalTaskStatus({ newStatus: 'completed' }), () => {
                            setShowLoader(false);
                            goToNextTask();
                        });
                        break;
                    case 'action':
                        switch (currentTaskInfo.answerType) {
                            case 'text':
                            case 'validated-text':
                                if (currentTaskInfo.answerType === 'validated-text' &&
                                    currentTaskResult.status === 'failed') {
                                    goToNextTask();
                                }
                                else {
                                    const inputTaskValidationErrors = validateInputField({
                                        fieldName: 'inputTaskAnswer',
                                        value: currentInputTaskAnswer,
                                    });
                                    if (!inputTaskValidationErrors) {
                                        setShowLoader(true);
                                        await logCurrentTaskTime();
                                        await handleInputTaskAnswerSubmit();
                                        setShowLoader(false);
                                        setShowTaskResult(true);
                                    }
                                }
                                break;
                            case 'single-selection':
                            case 'multiple-selection':
                            case 'player-selection':
                                const selectedAnswers = currentSelectionTaskAnswers?.filter((answer) => answer.isSelected);
                                const selectionTaskValidationErrors = validateSelect('selectionTaskAnswer', !!selectedAnswers?.length);
                                if (!selectionTaskValidationErrors) {
                                    setShowLoader(true);
                                    await logCurrentTaskTime();
                                    await handleSelectionTaskAnswerSubmit(selectedAnswers);
                                    setShowLoader(false);
                                    setShowTaskResult(true);
                                }
                                break;
                            case 'upload':
                                const photoUploadTaskValidationErrors = validateSelect('photoUploadTaskAnswer', !!currentPhotoUploadBlob);
                                if (!photoUploadTaskValidationErrors) {
                                    setShowLoader(true);
                                    await logCurrentTaskTime();
                                    await handlePhotoUploadTaskAnswerSubmit();
                                    setShowLoader(false);
                                    setShowTaskResult(true);
                                }
                                break;
                        }
                        break;
                }
            }
            else {
                goToNextTask();
            }
        }
    };
    const onTryAgainButtonClick = async () => {
        setShowTaskResult(false);
        dispatch(setCurrentTaskTimeLogs({
            taskUid: currentTaskId,
            startTime: getAtomFormattedDate(Date.now()),
        }));
    };
    return (_jsxs(ButtonsGroup, { justify: `${isLinearRally && currentTaskInfo?.taskType !== 'check-in' ? 'between' : 'end'}`, children: [isLinearRally && currentTaskInfo?.taskType !== 'check-in' && (_jsx(Button, { label: t('button.save_and_exit'), variant: 'outlined', onClick: async () => {
                    setShowLoader(true);
                    await logCurrentTaskTime();
                    setShowLoader(false);
                    navigate(generateIdRoutePath(APP_ROUTES.rallyGameResult, rallyPathId));
                } })), _jsxs(ButtonsGroup, { justify: 'end', children: [isLinearRally ? (currentTaskInfo?.taskType === 'check-in' ? (_jsx(Button, { label: t('button.skip'), variant: 'outlined', onClick: onTaskSkipButtonClick, disabled: showLoader })) : null) : (currentTaskResult?.status !== 'completed' &&
                        currentTaskInfo?.taskType !== 'information' &&
                        !showTaskResult && (_jsx(Button, { label: t('button.skip'), variant: 'outlined', onClick: onTaskSkipButtonClick, disabled: showLoader }))), showTaskResult &&
                        ((currentTaskInfo.taskType === 'action' &&
                            (currentTaskInfo?.answerType === 'single-selection' ||
                                currentTaskInfo?.answerType === 'multiple-selection') &&
                            currentTaskResult.status === 'failed') ||
                            (currentTaskInfo.taskType === 'action' &&
                                currentTaskInfo?.answerType === 'validated-text' &&
                                currentTaskResult.status === 'failed_attempt')) && (_jsx(Button, { onClick: onTryAgainButtonClick, label: t('button.try_again'), variant: 'outlined' })), currentTaskInfo?.taskType === 'check-in' ? (currentTaskResult?.status === 'completed' ? (_jsx(Button, { label: t('button.next'), onClick: onTaskNextButtonClick, disabled: showLoader })) : null) : isLinearRally ? (showTaskResult &&
                        ((currentTaskResult.status === 'failed' &&
                            currentTaskInfo.answerType !== 'validated-text') ||
                            currentTaskResult.status === 'failed_attempt') ? null : (_jsx(Button, { label: t('button.next'), onClick: onTaskNextButtonClick, disabled: showLoader }))) : (_jsx(Button, { label: t('button.next'), onClick: onTaskNextButtonClick, disabled: showLoader }))] })] }));
};
export default GameplayControls;
